import { createReducer, ActionType, createAsyncAction } from "typesafe-actions";
import {
  UserDto,
  UsersListDto,
  GetUserListQuery,
  UserUpdateDto,
  ResultOfBoolean,
  EmployeeDto,
} from "Api/Api";

export type UsersState = {
  users: UsersListDto;
  userDetail: UserDto | null;
  isLoading: boolean;
  isDeleting: boolean;
  isUpdating: boolean;
  notValid: boolean;
  errorCode: string | null;
  loggedUserInfo: EmployeeDto | null;
};

export const getUsersAsync = createAsyncAction(
  "@user/GET_USERS_REQUEST",
  "@user/GET_USERS_SUCCESS",
  "@user/GET_USERS_FAILURE",
)<GetUserListQuery, UsersListDto, Error>();

export const getUsersDetailAsync = createAsyncAction(
  "@user/GET_USER_DETAIL_REQUEST",
  "@user/GET_USER_DETAIL_SUCCESS",
  "@user/GET_USER_DETAIL_FAILURE",
)<number, UserDto, Error>();

export const getLoggedUserInfoAsync = createAsyncAction(
  "@user/GET_LOGGED_USER_INFO_REQUEST",
  "@user/GET_LOGGED_USER_INFO_SUCCESS",
  "@user/GET_LOGGED_USER_INFO_FAILURE",
)<void, EmployeeDto, Error>();

export const deleteUsersAsync = createAsyncAction(
  "@user/DELETE_USER_REQUEST",
  "@user/DELETE_USER_SUCCESS",
  "@user/DELETE_USER_FAILURE",
)<number, boolean, Error>();

export const updateUserDetailAsync = createAsyncAction(
  "@user/UPDATE_STATUS_USER_DETAIL_REQUEST",
  "@user/UPDATE_STATUS_USER_DETAIL_SUCCESS",
  "@user/UPDATE_STATUS_USER_DETAIL_FAILURE",
)<UserUpdateDto, { result: ResultOfBoolean; user: UserUpdateDto }, Error>();

export type UserAction =
  | ActionType<typeof getUsersAsync>
  | ActionType<typeof deleteUsersAsync>
  | ActionType<typeof updateUserDetailAsync>
  | ActionType<typeof getUsersDetailAsync>
  | ActionType<typeof getLoggedUserInfoAsync>;

export const userReducer = createReducer<UsersState, UserAction>({
  users: {
    count: 0,
    users: [],
  },
  userDetail: null,
  isLoading: false,
  isDeleting: false,
  isUpdating: false,
  notValid: false,
  errorCode: null,
  loggedUserInfo: null,
})
  .handleAction(getUsersAsync.request, state => {
    return { ...state, isLoading: true };
  })
  .handleAction(getUsersAsync.success, (state, action) => {
    return {
      ...state,
      users: action.payload,
      errorCode: null,
      isLoading: false,
    };
  })
  .handleAction(getUsersAsync.failure, (state, action) => {
    return {
      ...state,
      errorCode: action.payload.message,
      isLoading: false,
    };
  })

  .handleAction(getUsersDetailAsync.request, state => {
    return {
      ...state,
      isLoading: true,
    };
  })
  .handleAction(getUsersDetailAsync.success, (state, action) => {
    return {
      ...state,
      userDetail: action.payload,
      isLoading: false,
    };
  })
  .handleAction(getUsersDetailAsync.failure, (state, action) => {
    return {
      ...state,
      isLoading: false,
      errorCode: action.payload.name,
    };
  })

  .handleAction(getLoggedUserInfoAsync.request, state => {
    return {
      ...state,
      isLoading: true,
    };
  })
  .handleAction(getLoggedUserInfoAsync.success, (state, action) => {
    return {
      ...state,
      loggedUserInfo: action.payload,
      isLoading: false,
    };
  })
  .handleAction(getLoggedUserInfoAsync.failure, (state, action) => {
    return {
      ...state,
      isLoading: false,
      errorCode: action.payload.name,
    };
  })

  .handleAction(deleteUsersAsync.request, state => {
    return {
      ...state,
      isDeleting: true,
    };
  })
  .handleAction(deleteUsersAsync.success, state => {
    return {
      ...state,
      isDeleting: false,
    };
  })
  .handleAction(deleteUsersAsync.failure, (state, action) => {
    return {
      ...state,
      isDeleting: false,
      errorCode: action.payload.name,
    };
  })

  .handleAction(updateUserDetailAsync.request, state => {
    return {
      ...state,
      notValid: false,
      isUpdating: true,
    };
  })
  .handleAction(updateUserDetailAsync.success, (state, action) => {
    if (!action.payload.result.isValid) {
      return {
        ...state,
        notValid: true,
      };
    }

    return {
      ...state,
      notValid: false,
      isUpdating: false,
    };
  })
  .handleAction(updateUserDetailAsync.failure, (state, action) => {
    return {
      ...state,
      isUpdating: false,
      errorCode: action.payload.name,
    };
  });
